import React, { useEffect, useState } from 'react';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { CollapseBlock, Spinner } from '../../../../common';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import PhdStudentEvaluationsTable from './PhdStudentEvaluationsTable.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useDeletePhdStudentEvaluationGradeMutation,
  useGetPhdStudentEvaluationGradesQuery,
  useLazyGetPhdStudentEvaluationDocumentQuery,
  useArchivePhdStudentEvaluationDocumentMutation,
} from '../../../../../redux/services/phd';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import PhdArchiveFilter, { usePhdArchiveStatus } from '../PhdArchiveFilter.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';

const PhdStudentEvaluationGradesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useIsUserPhdAdmin();
  const { deleteSuccess, deleteFailed } = useToastMessages();
  const { archiveStatus, setArchiveStatus } = usePhdArchiveStatus(
    'phd-student-evaluation-grades-archive-status',
  );
  const [pagination, setPagination] = useState({ page: 1, per_page: 25 });

  const {
    data: studentEvaluations,
    isLoading,
    error: loadError,
    refetch,
  } = useGetPhdStudentEvaluationGradesQuery(
    {
      archive_status: archiveStatus,
      paginated: 1,
      page: pagination.page,
      per_page: pagination.per_page,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [deleteEvaluation] = useDeletePhdStudentEvaluationGradeMutation();
  const [downloadEvaluationDoc, { isLoading: isDownloading }] =
    useLazyGetPhdStudentEvaluationDocumentQuery();
  const [archiveEvaluationDoc, { isLoading: isArchiving }] =
    useArchivePhdStudentEvaluationDocumentMutation();

  const pageTitle = t('Zadanie známok doktorandom');
  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const handleDelete = async (row) => {
    try {
      await deleteEvaluation(row.id).unwrap();
      deleteSuccess();
      refetch();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleDownload = async (row) => {
    if (!row?.linked_evaluation_id) {
      return;
    }

    try {
      const response = row.linked_evaluation_is_frozen
        ? await downloadEvaluationDoc(row.linked_evaluation_id).unwrap()
        : await archiveEvaluationDoc(row.linked_evaluation_id).unwrap();
      downloadBlobResponse(
        response,
        `${row.student_display_name || t('Hodnotenie študenta')}.docx`,
      );
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleArchiveStatusChange = (value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setArchiveStatus(value);
  };

  const handlePaginationChange = (nextPagination) => {
    setPagination({
      page: nextPagination.current,
      per_page: nextPagination.pageSize,
    });
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title={getPhdApiErrorMessage(
          loadError,
          t(
            'PhD údaje sa nepodarilo načítať. Obnovte stránku alebo skúste znova.',
          ),
        )}
      />
    );
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať zadanie známok doktorandovi')}
        createUrl={routes.cmsPhdStudentEvaluationGradesCreate}
        permissionsBasic="phd-student-evaluation-grades"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje zadávanie známok?')}
          description={t(
            'Administrátor doktorandského štúdia eviduje výsledky predmetov pre vybraný školský rok. Odovzdané známky potom tvoria podklad ročného hodnotenia doktoranda.',
          )}
        />
        <PhdArchiveFilter
          archiveStatus={archiveStatus}
          onChange={handleArchiveStatusChange}
          storageKey="phd-student-evaluation-grades-archive-status"
        />
        <CollapseBlock>
          <PhdStudentEvaluationsTable
            studentEvaluations={studentEvaluations?.data || []}
            onDetailClick={(row) =>
              navigate(`${routes.cmsPhdStudentEvaluationGradesEdit}/${row.id}`)
            }
            onDelete={handleDelete}
            onDownload={handleDownload}
            canDownloadRow={(row) =>
              Boolean(row?.linked_evaluation_is_frozen || isAdmin)
            }
            isDownloading={isDownloading || isArchiving}
            serviceMode="grades"
            pagination={{
              current: studentEvaluations?.current_page ?? pagination.page,
              pageSize: studentEvaluations?.per_page ?? pagination.per_page,
              total: studentEvaluations?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50, 100],
            }}
            onPaginationChange={handlePaginationChange}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default PhdStudentEvaluationGradesPage;
